import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { 
  Plus, Edit2, Trash2, Search, X, Loader2, RefreshCw, 
  CheckCircle2, XCircle, AlertCircle, Layers 
} from 'lucide-react';
import { toast } from 'react-toastify';
import api from '../../common/apiClient';
import SummaryApi from '../../common/SummaryApi';
import SelectDropdown from '../../customStyles/SelectDropdown';

const OurProductBusinessCategories = () => {
  const [categories, setCategories] = useState([]);
  const [productList, setProductList] = useState([]);
  const [loading, setLoading] = useState(true);

  // Filters
  const [selectedProductFilter, setSelectedProductFilter] = useState(''); // empty = all
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  // Modal State for Add / Edit
  const [modalOpen, setModalOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [modalLoading, setModalLoading] = useState(false);
  const [formData, setFormData] = useState({
    productId: '',
    name: '',
    code: '',
    description: '',
    isActive: true,
  });
  const [formErrors, setFormErrors] = useState({});

  // Delete Confirmation Modal State
  const [deleteModal, setDeleteModal] = useState({ isOpen: false, item: null, loading: false });

  // 1. Fetch Products for dropdown selection
  const fetchProductsList = useCallback(async () => {
    try {
      const response = await api.get(SummaryApi.getAllProducts.url);
      if (response.data?.success) {
        setProductList(response.data.data || []);
      }
    } catch (error) {
      console.error('Error fetching products list:', error);
    }
  }, []);

  // 2. Fetch Business Categories based on current Product Filter
  const fetchCategories = useCallback(async () => {
    setLoading(true);
    try {
      let url;
      if (typeof SummaryApi.getAllBusinessCategories?.url === 'function') {
        url = SummaryApi.getAllBusinessCategories.url(selectedProductFilter || '', '');
      } else {
        url = SummaryApi.getAllBusinessCategories.url;
      }

      const response = await api.get(url, {
        params: {
          productId: selectedProductFilter || undefined,
        }
      });

      if (response.data?.success) {
        setCategories(response.data.data || []);
      }
    } catch (error) {
      console.error('Error fetching business categories:', error);
    } finally {
      setLoading(false);
    }
  }, [selectedProductFilter]);

  useEffect(() => {
    fetchProductsList();
  }, [fetchProductsList]);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  // Options for Dropdowns
  const productFilterOptions = useMemo(() => [
    // { value: '', label: 'Select ERP Products' },
    ...productList.map(p => ({ value: p._id, label: p.name }))
  ], [productList]);

  const productModalOptions = useMemo(() => 
    productList.map(p => ({ value: p._id, label: p.name }))
  , [productList]);

  const statusOptions = useMemo(() => [
    { value: 'all', label: 'All Status' },
    { value: 'active', label: 'Active Only' },
    { value: 'inactive', label: 'Inactive' },
  ], []);

  // Open Modal for Create
  const handleOpenCreate = () => {
    setEditingCategory(null);
    setFormData({
      productId: selectedProductFilter || (productList[0]?._id || ''),
      name: '',
      code: '',
      description: '',
      isActive: true,
    });
    setFormErrors({});
    setModalOpen(true);
  };

  // Open Modal for Edit
  const handleOpenEdit = (category) => {
    setEditingCategory(category);
    setFormData({
      productId: category.productId?._id || category.productId || '',
      name: category.name || '',
      code: category.code || '',
      description: category.description || '',
      isActive: category.isActive !== undefined ? category.isActive : true,
    });
    setFormErrors({});
    setModalOpen(true);
  };

  // Form Validation
  const validate = () => {
    const errs = {};
    if (!formData.productId) errs.productId = 'Parent ERP Product is required';
    if (!formData.name.trim()) errs.name = 'Business Category name is required';
    setFormErrors(errs);
    return Object.keys(errs).length === 0;
  };

  // Save (Create or Update)
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setModalLoading(true);
    try {
      const payload = {
        productId: formData.productId,
        name: formData.name.trim(),
        code: formData.code.trim().toUpperCase(),
        description: formData.description.trim(),
        isActive: formData.isActive,
      };

      let response;
      if (editingCategory) {
        const url = typeof SummaryApi.updateBusinessCategory.url === 'function'
          ? SummaryApi.updateBusinessCategory.url(editingCategory._id)
          : SummaryApi.updateBusinessCategory.url;
        response = await api.put(url, payload);
      } else {
        response = await api.post(SummaryApi.createBusinessCategory.url, payload);
      }

      if (response.data?.success) {
        toast.success(editingCategory ? 'Business Category updated' : 'Business Category created');
        setModalOpen(false);
        fetchCategories();
      } else {
        toast.error(response.data?.message || 'Operation failed');
      }
    } catch (error) {
      console.error('Error saving business category:', error);
      toast.error(error.response?.data?.message || 'Error occurred while saving');
    } finally {
      setModalLoading(false);
    }
  };

  // Delete Action
  const handleDeleteConfirm = async () => {
    if (!deleteModal.item) return;

    setDeleteModal(prev => ({ ...prev, loading: true }));
    try {
      const url = typeof SummaryApi.deleteBusinessCategory.url === 'function'
        ? SummaryApi.deleteBusinessCategory.url(deleteModal.item._id)
        : SummaryApi.deleteBusinessCategory.url;

      const response = await api.delete(url);
      if (response.data?.success) {
        toast.success('Business category deleted successfully');
        setDeleteModal({ isOpen: false, item: null, loading: false });
        fetchCategories();
      } else {
        toast.error(response.data?.message || 'Delete failed');
      }
    } catch (error) {
      console.error('Error deleting category:', error);
      toast.error(error.response?.data?.message || 'Failed to delete category');
    } finally {
      setDeleteModal(prev => ({ ...prev, loading: false }));
    }
  };

  // Filter Categories on Client Side
const filteredCategories = useMemo(() => {
  const term = searchTerm.toLowerCase().trim();

  return categories.filter((item) => {
    const matchSearch =
      !term ||
      Boolean(item.name && item.name.toLowerCase().includes(term)) ||
      Boolean(item.code && item.code.toLowerCase().includes(term)) ||
      Boolean(item.description && item.description.toLowerCase().includes(term));

    const matchStatus =
      !statusFilter || statusFilter === 'all'
        ? true
        : statusFilter === 'active'
          ? Boolean(item.isActive)
          : !item.isActive;

    return matchSearch && matchStatus;
  });
}, [categories, searchTerm, statusFilter]);

  return (
    <div className="w-full p-4 md:p-6 bg-gray-50 min-h-screen">
      {/* Top Header Card */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 md:p-6 mb-6">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-red-50 text-primary rounded-lg">
              <Layers className="w-6 h-6" />
            </div>
            <div>
              <h1 className="text-xl md:text-2xl font-bold text-gray-900">Product Business Categories</h1>
              <p className="text-xs md:text-sm text-gray-500">
                Map business categories and industry verticals under each ERP product
              </p>
            </div>
          </div>

          <button
            onClick={handleOpenCreate}
            className="flex items-center justify-center gap-2 bg-primary text-white px-4 py-2.5 rounded-lg text-sm font-medium shadow-sm transition-colors"
          >
            <Plus className="w-4 h-4" />
            <span>Add Business Category</span>
          </button>
        </div>

        {/* Filter Bar */}
        <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3 pt-4 border-t border-gray-100">
          {/* Filter by Product Dropdown */}
          <div className="sm:col-span-2 md:col-span-1">
            <label className="block text-xs font-semibold text-gray-600 uppercase mb-1">Filter By Product</label>
            <SelectDropdown
              options={productFilterOptions}
              value={selectedProductFilter}
              onChange={(value) => setSelectedProductFilter(value)}
              placeholder="All ERP Products"
              valueKey="value"
              labelKey="label"
              searchable={true}
            />
          </div>

          {/* Search Term */}
          <div className="sm:col-span-2 md:col-span-2">
            <label className="block text-xs font-semibold text-gray-600 uppercase mb-1">Search</label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search category name, code..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-9 pr-8 py-2 text-sm bg-gray-50 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:bg-white"
              />
              {searchTerm && (
                <button 
                  onClick={() => setSearchTerm('')} 
                  className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>

          {/* Status & Refresh */}
          <div className="flex items-end gap-2">
            <div className="flex-1">
              <label className="block text-xs font-semibold text-gray-600 uppercase mb-1">Status</label>
              <SelectDropdown
                options={statusOptions}
                value={statusFilter}
                onChange={(value) => setStatusFilter(value)}
                placeholder="All Status"
                valueKey="value"
                labelKey="label"
                searchable={false}
              />
            </div>
            <button
              onClick={fetchCategories}
              disabled={loading}
              className="p-2.5 border border-gray-300 hover:bg-gray-100 rounded-lg text-gray-600 transition-colors"
              title="Refresh"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>
      </div>

      {/* Table Data */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        {loading ? (
          <div className="flex flex-col items-center justify-center p-12">
            <Loader2 className="w-8 h-8 text-primary animate-spin mb-3" />
            <p className="text-sm text-gray-500">Loading business categories...</p>
          </div>
        ) : filteredCategories.length === 0 ? (
          <div className="text-center p-12">
            <Layers className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <h3 className="text-base font-semibold text-gray-700">No business categories found</h3>
            <p className="text-xs text-gray-400 mt-1">
              {selectedProductFilter 
                ? 'No categories added for this product yet. Click Add to create one.' 
                : 'Click "Add Business Category" to get started'}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 text-left text-sm">
              <thead className="bg-gray-50 text-gray-600 font-medium">
                <tr>
                  <th className="py-3.5 px-4 w-14 text-center">S.No</th>
                  <th className="py-3.5 px-4">Business Category Name</th>
                  <th className="py-3.5 px-4">Parent ERP Product</th>
                  <th className="py-3.5 px-4">Category Code</th>
                  <th className="py-3.5 px-4">Description</th>
                  <th className="py-3.5 px-4 text-center">Status</th>
                  <th className="py-3.5 px-4 text-center w-28">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filteredCategories.map((cat, idx) => (
                  <tr key={cat._id} className="hover:bg-gray-50/70 transition-colors">
                    <td className="py-3.5 px-4 text-center text-gray-500">{idx + 1}</td>
                    <td className="py-3.5 px-4">
                      <span className="font-semibold text-gray-900">{cat.name}</span>
                    </td>
                    <td className="py-3.5 px-4">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-red-50 text-primary border border-red-200">
                        {cat.productId?.name || 'Assigned Product'}
                      </span>
                    </td>
                    <td className="py-3.5 px-4">
                      {cat.code ? (
                        <span className="inline-block px-2 py-0.5 font-mono text-xs font-semibold bg-gray-100 text-gray-800 rounded">
                          {cat.code}
                        </span>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </td>
                    <td className="py-3.5 px-4 text-gray-500 max-w-xs truncate">
                      {cat.description || '-'}
                    </td>
                    <td className="py-3.5 px-4 text-center">
                      <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        cat.isActive 
                          ? 'bg-green-100 text-green-700' 
                          : 'bg-red-100 text-red-700'
                      }`}>
                        {cat.isActive ? <CheckCircle2 className="w-3 h-3" /> : <XCircle className="w-3 h-3" />}
                        {cat.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="py-3.5 px-4">
                      <div className="flex items-center justify-center gap-2">
                        <button
                          onClick={() => handleOpenEdit(cat)}
                          className="p-1.5 text-primary hover:bg-red-50 rounded-lg transition-colors"
                          title="Edit Category"
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => setDeleteModal({ isOpen: true, item: cat, loading: false })}
                          className="p-1.5 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                          title="Delete Category"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Add / Edit Category Modal */}
      {modalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-fade-in">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md overflow-hidden">
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
              <h3 className="text-lg font-bold text-gray-900">
                {editingCategory ? 'Edit Business Category' : 'Add Business Category'}
              </h3>
              <button 
                onClick={() => setModalOpen(false)}
                className="text-gray-400 hover:text-gray-600 rounded-lg p-1"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-5 space-y-4">
              <div>
                <label className="block text-xs font-semibold text-gray-700 uppercase mb-1">
                  Parent ERP Product <span className="text-red-500">*</span>
                </label>
                <SelectDropdown
                  options={productModalOptions}
                  value={formData.productId}
                  onChange={(value) => setFormData(prev => ({ ...prev, productId: value }))}
                  placeholder="Select Product"
                  valueKey="value"
                  labelKey="label"
                  searchable={true}
                  zIndex={71}
                  error={formErrors.productId}
                />
                {formErrors.productId && <p className="text-red-500 text-xs mt-1">{formErrors.productId}</p>}
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-700 uppercase mb-1">
                  Category Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  placeholder="e.g. Bespoke Tailoring / Boutique"
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  className={`w-full px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 ${
                    formErrors.name ? 'border-red-500' : 'border-gray-300'
                  }`}
                />
                {formErrors.name && <p className="text-red-500 text-xs mt-1">{formErrors.name}</p>}
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-700 uppercase mb-1">
                  Category Code
                </label>
                <input
                  type="text"
                  placeholder="e.g. CAT-BESPOKE"
                  value={formData.code}
                  onChange={(e) => setFormData(prev => ({ ...prev, code: e.target.value.toUpperCase() }))}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 uppercase font-mono"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-700 uppercase mb-1">
                  Description
                </label>
                <textarea
                  rows="3"
                  placeholder="Business vertical notes or specifications..."
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 resize-none"
                />
              </div>

              <div className="flex items-center gap-2 pt-1">
                <input
                  type="checkbox"
                  id="isCatActiveToggle"
                  checked={formData.isActive}
                  onChange={(e) => setFormData(prev => ({ ...prev, isActive: e.target.checked }))}
                  className="w-4 h-4 text-primary rounded border-gray-300 focus:ring-red-500 cursor-pointer"
                />
                <label htmlFor="isCatActiveToggle" className="text-sm font-medium text-gray-700 cursor-pointer select-none">
                  Active Category
                </label>
              </div>

              <div className="flex justify-end gap-2 pt-4 border-t border-gray-100">
                <button
                  type="button"
                  onClick={() => setModalOpen(false)}
                  disabled={modalLoading}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={modalLoading}
                  className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-white bg-primary rounded-lg transition-colors shadow-sm disabled:opacity-60"
                >
                  {modalLoading && <Loader2 className="w-4 h-4 animate-spin" />}
                  <span>{editingCategory ? 'Update Category' : 'Create Category'}</span>
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteModal.isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-fade-in">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-sm p-5 text-center">
            <div className="w-12 h-12 bg-red-100 text-red-600 rounded-full flex items-center justify-center mx-auto mb-3">
              <AlertCircle className="w-6 h-6" />
            </div>
            <h3 className="text-base font-bold text-gray-900 mb-1">Delete Business Category?</h3>
            <p className="text-xs text-gray-500 mb-4">
              Are you sure you want to delete <strong className="text-gray-800">{deleteModal.item?.name}</strong>?
            </p>
            <div className="flex justify-center gap-2">
              <button
                type="button"
                onClick={() => setDeleteModal({ isOpen: false, item: null, loading: false })}
                disabled={deleteModal.loading}
                className="px-4 py-2 text-xs font-semibold text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleDeleteConfirm}
                disabled={deleteModal.loading}
                className="flex items-center gap-1.5 px-4 py-2 text-xs font-semibold text-white bg-red-600 hover:bg-red-700 rounded-lg shadow-sm disabled:opacity-60"
              >
                {deleteModal.loading && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                <span>Delete Category</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OurProductBusinessCategories;